import React from "react";

// Drawn in viewBox units; the SVG scales to its container width.
const WIDTH = 600;
const PADDING_X = 24;
const LEVEL_SPACING = 30;
const BAR_HEIGHT = 22;
const BAR_GAP = 8;
const SECTION_GAP = 56;
const LABEL_COLUMN = 150;
// Left strip of the buy-levels chart reserved for the total-fall bracket.
const AXIS_GUTTER = 18;

const ordinal = n => {
    const lastTwo = n % 100;
    if (lastTwo >= 11 && lastTwo <= 13) return `${n}th`;
    return `${n}${{ 1: "st", 2: "nd", 3: "rd" }[n % 10] || "th"}`;
};

// 0 → "X", 1 → "X+Y", 2 → "X+2Y", …
const investmentFormula = index => (index === 0 ? "X" : `X+${index === 1 ? "" : index}Y`);

const formatPrice = value => value.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatRupees = value => `₹${value.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

// Top: one horizontal line per buy level, with the falling price path running through them.
// Bottom: one centred bar per buy, width ∝ amount invested — widening downward into a pyramid when Y > 0.
const PyramidDiagram = ({ purchases, averagePrice, awayPercent, totalFallPercent, totalFallPoints }) => {
    if (!purchases.length) {
        return (
            <div className="pyramid-diagram pyramid-diagram-empty">Add at least one purchase to see the diagram.</div>
        );
    }

    const prices = purchases.map(purchase => purchase.price);
    const maxPrice = Math.max(...prices);
    const minPrice = Math.min(...prices);
    const priceRange = maxPrice - minPrice;

    const ladderTop = 40;
    const ladderHeight = (purchases.length - 1) * LEVEL_SPACING;
    const lineRight = WIDTH - PADDING_X - LABEL_COLUMN;
    const plotLeft = PADDING_X + AXIS_GUTTER;
    const pathLeft = plotLeft + 16;
    const pathRight = lineRight - 16;
    const levelY = price =>
        priceRange > 0 ? ladderTop + ((maxPrice - price) / priceRange) * ladderHeight : ladderTop + ladderHeight / 2;
    const pointX = index =>
        purchases.length > 1 ? pathLeft + (index / (purchases.length - 1)) * (pathRight - pathLeft) : pathLeft;

    const barsTop = ladderTop + ladderHeight + SECTION_GAP;
    const maxInvestment = Math.max(...purchases.map(purchase => purchase.investment), 0);
    const maxBarWidth = WIDTH - PADDING_X * 2;
    const barWidth = investment => (maxInvestment > 0 ? Math.max(0, investment / maxInvestment) * maxBarWidth : 0);
    const height = barsTop + purchases.length * (BAR_HEIGHT + BAR_GAP) + 8;

    const averageY = levelY(averagePrice);
    const ladderBottom = ladderTop + ladderHeight;
    const fallAxisX = PADDING_X + 6;
    // Total-fall label runs along the price path, centred on the chord from first to last buy and
    // lifted off it. Compounding drops bow the path below that chord, so the label stays clear of it.
    const fallStartX = pointX(0);
    const fallEndX = pointX(purchases.length - 1);
    const fallMidX = (fallStartX + fallEndX) / 2;
    const fallMidY = (ladderTop + ladderBottom) / 2;
    const fallAngle = (Math.atan2(ladderBottom - ladderTop, fallEndX - fallStartX) * 180) / Math.PI;
    const FALL_LABEL_LIFT = 14;
    const pathPoints = purchases.map((purchase, index) => `${pointX(index)},${levelY(purchase.price)}`).join(" ");

    return (
        <div className="pyramid-diagram">
            <svg
                viewBox={`0 0 ${WIDTH} ${height}`}
                role="img"
                aria-label={`Buy levels from ${formatPrice(maxPrice)} down to ${formatPrice(
                    minPrice,
                )}, investing ${formatRupees(purchases[0].investment)} up to ${formatRupees(
                    purchases[purchases.length - 1].investment,
                )}`}
            >
                <text className="pyramid-diagram-heading" x={PADDING_X} y={18}>
                    Buy levels
                </text>
                {purchases.map((purchase, index) => {
                    const y = levelY(purchase.price);
                    return (
                        <g key={purchase.dropNumber}>
                            <line className="pyramid-diagram-level" x1={plotLeft} x2={lineRight} y1={y} y2={y} />
                            <text className="pyramid-diagram-label" x={lineRight + 12} y={y + 4}>
                                <tspan className="pyramid-diagram-label-strong">{ordinal(index + 1)} buy</tspan>
                                {`  ${formatPrice(purchase.price)}`}
                            </text>
                        </g>
                    );
                })}
                {purchases.length > 1 && (
                    <g className="pyramid-diagram-fall">
                        <title>{`Total fall entertained: ${totalFallPercent.toFixed(2)}% (${formatPrice(
                            totalFallPoints,
                        )} pts), from ${formatPrice(maxPrice)} to ${formatPrice(minPrice)}`}</title>
                        <line x1={fallAxisX} x2={fallAxisX} y1={ladderTop} y2={ladderBottom} />
                        <line x1={fallAxisX - 4} x2={fallAxisX + 4} y1={ladderTop} y2={ladderTop} />
                        <line x1={fallAxisX - 4} x2={fallAxisX + 4} y1={ladderBottom} y2={ladderBottom} />
                    </g>
                )}
                <polyline className="pyramid-diagram-path" points={pathPoints} />
                {purchases.length > 1 && (
                    <text
                        className="pyramid-diagram-label"
                        x={fallMidX}
                        y={fallMidY - FALL_LABEL_LIFT}
                        textAnchor="middle"
                        transform={`rotate(${fallAngle} ${fallMidX} ${fallMidY})`}
                    >
                        <tspan className="pyramid-diagram-label-strong">Total fall</tspan>
                        {`  ${totalFallPercent.toFixed(2)}%  ·  ${formatPrice(totalFallPoints)} pts`}
                    </text>
                )}
                {/* Label sits below-left of the line: the path only crosses that corner above the average. */}
                <g className="pyramid-diagram-average">
                    <title>{`Average buying price ${formatPrice(averagePrice)}, ${awayPercent.toFixed(2)}% above the last buy`}</title>
                    <line x1={plotLeft} x2={lineRight} y1={averageY} y2={averageY} />
                    <text className="pyramid-diagram-label" x={plotLeft} y={averageY + 15}>
                        <tspan className="pyramid-diagram-label-strong">Avg</tspan>
                        {`  ${formatPrice(averagePrice)}  ·  ${awayPercent.toFixed(2)}% above last buy`}
                    </text>
                </g>
                {purchases.map((purchase, index) => (
                    <g className="pyramid-diagram-point" key={purchase.dropNumber}>
                        <title>{`${ordinal(index + 1)} buy at ${formatPrice(purchase.price)} — ${formatRupees(
                            purchase.investment,
                        )}`}</title>
                        <circle className="pyramid-diagram-hit" cx={pointX(index)} cy={levelY(purchase.price)} r={12} />
                        <circle className="pyramid-diagram-dot" cx={pointX(index)} cy={levelY(purchase.price)} r={5} />
                    </g>
                ))}

                <text className="pyramid-diagram-heading" x={PADDING_X} y={barsTop - 16}>
                    Investment per buy
                </text>
                {purchases.map((purchase, index) => {
                    const width = barWidth(purchase.investment);
                    const y = barsTop + index * (BAR_HEIGHT + BAR_GAP);
                    return (
                        <g className="pyramid-diagram-bar-group" key={purchase.dropNumber}>
                            <title>{`${ordinal(index + 1)} buy: ${investmentFormula(index)} = ${formatRupees(
                                purchase.investment,
                            )} at ${formatPrice(purchase.price)}`}</title>
                            <rect
                                className="pyramid-diagram-bar"
                                x={(WIDTH - width) / 2}
                                y={y}
                                width={width}
                                height={BAR_HEIGHT}
                                rx={4}
                            />
                            <text
                                className="pyramid-diagram-bar-label"
                                x={WIDTH / 2}
                                y={y + BAR_HEIGHT / 2 + 4}
                                textAnchor="middle"
                            >
                                <tspan className="pyramid-diagram-label-strong">{investmentFormula(index)}</tspan>
                                {`  ${formatRupees(purchase.investment)}`}
                            </text>
                        </g>
                    );
                })}
            </svg>
        </div>
    );
};

export default PyramidDiagram;
